/**
 * Workflow Schema Helpers
 * 
 * Utility functions that provide schema-driven workflow behavior
 * instead of hardcoded workflow-specific checks.
 * 
 * These replace hardcoded functions like:
 * - isLeaseAbstraction()
 * - isOceanbankStepOne()
 * - isMultiStepWorkflow()
 */

import { WorkflowUiSchema, InputDisplayConfig, FileUploadConfig } from '../services/schemaService';

/**
 * Output handling configuration from UI schema
 */
export interface OutputHandling {
  primaryOutputType?: string;
  showDownloadForTypes?: string[];
  hideOutputTypes?: string[];
  downloadFilenameTemplate?: string;
}

/**
 * Multi-step workflow configuration from UI schema
 */
export interface MultiStepConfig {
  nextWorkflow?: {
    practice: string;
    workflow: string;
  };
  reviewRequired?: boolean;
  autoContinue?: boolean;
}

/**
 * Completion actions configuration from UI schema
 */
export interface CompletionActions {
  showReviewButton?: boolean;
  reviewButtonText?: string;
  completionMessage?: string;
  continueButtonText?: string;
}

/**
 * Chained workflow configuration (for recipe.json)
 */
export interface ChainedWorkflowConfig {
  enabled?: boolean;
  steps?: string[];
  autoChain?: boolean;
  passOutputAsInput?: boolean;
  inputFileFilter?: string[];
}

/**
 * Get output handling configuration from UI schema
 * When no schema exists, applies sensible defaults: hide intermediate
 * processing artifacts (json, txt) and only surface final deliverables
 */
export function getOutputHandling(uiSchema: WorkflowUiSchema | null): OutputHandling {
  if (!uiSchema?.output_handling) {
    // sensible defaults when no ui-schema is configured —
    // hide intermediate V2 step artifacts that confuse end users
    return {
      hideOutputTypes: ['json', 'txt'],
    };
  }
  
  return {
    primaryOutputType: uiSchema.output_handling.primary_output_type,
    showDownloadForTypes: uiSchema.output_handling.show_download_for_types,
    hideOutputTypes: uiSchema.output_handling.hide_output_types,
    downloadFilenameTemplate: uiSchema.output_handling.download_filename_template,
  };
}

/**
 * Get multi-step configuration from UI schema
 * Replaces hardcoded isMultiStepWorkflow() checks
 */
export function getMultiStepConfig(uiSchema: WorkflowUiSchema | null): MultiStepConfig {
  if (!uiSchema?.multi_step_config) {
    // Check legacy format
    if (uiSchema?.continuation?.enabled) {
      return {
        nextWorkflow: uiSchema.continuation.next_workflow 
          ? { practice: uiSchema.metadata.practice, workflow: uiSchema.continuation.next_workflow }
          : undefined,
        reviewRequired: true,
        autoContinue: false,
      };
    }
    return {};
  }
  
  return {
    nextWorkflow: uiSchema.multi_step_config.next_workflow,
    reviewRequired: uiSchema.multi_step_config.review_required,
    autoContinue: uiSchema.multi_step_config.auto_continue,
  };
}

/**
 * Get completion actions from UI schema
 * Replaces hardcoded button text and messages
 */
export function getCompletionActions(uiSchema: WorkflowUiSchema | null): CompletionActions {
  if (!uiSchema?.completion_actions) {
    // Check legacy format
    if (uiSchema?.continuation?.review_screen) {
      return {
        showReviewButton: uiSchema.continuation.enabled,
        reviewButtonText: uiSchema.continuation.review_screen.submit_button_text || 'Review & Continue',
      };
    }
    return {};
  }
  
  return {
    showReviewButton: uiSchema.completion_actions.show_review_button,
    reviewButtonText: uiSchema.completion_actions.review_button_text,
    completionMessage: uiSchema.completion_actions.completion_message,
  };
}

/**
 * Check if this is a multi-step workflow (from UI schema)
 * Replaces: isMultiStepWorkflow() hardcoded check
 */
export function isMultiStepWorkflow(uiSchema: WorkflowUiSchema | null): boolean {
  const config = getMultiStepConfig(uiSchema);
  return config.nextWorkflow !== undefined;
}

/**
 * Check if review is required before continuing to next step
 * Replaces: hardcoded review_required checks
 */
export function requiresReview(uiSchema: WorkflowUiSchema | null): boolean {
  const config = getMultiStepConfig(uiSchema);
  // Review is required if explicitly set, or if auto_continue is false
  return config.reviewRequired === true || config.autoContinue === false;
}

/**
 * Check if output files of a certain type should be hidden
 * Replaces: hardcoded file filtering like isLeaseAbstraction() checks
 */
export function shouldHideOutputType(uiSchema: WorkflowUiSchema | null, fileType: string): boolean {
  const handling = getOutputHandling(uiSchema);
  return handling.hideOutputTypes?.includes(fileType.toLowerCase()) ?? false;
}

/**
 * Check if output files of a certain type should be shown for download
 * Replaces: hardcoded file filtering checks
 */
export function shouldShowOutputType(uiSchema: WorkflowUiSchema | null, fileType: string): boolean {
  const handling = getOutputHandling(uiSchema);
  
  // If hideOutputTypes is specified, check it first
  if (handling.hideOutputTypes?.includes(fileType.toLowerCase())) {
    return false;
  }
  
  // If showDownloadForTypes is specified, only show those types
  if (handling.showDownloadForTypes && handling.showDownloadForTypes.length > 0) {
    return handling.showDownloadForTypes.includes(fileType.toLowerCase());
  }
  
  // Default: show all types
  return true;
}

/**
 * Filter output files based on UI schema configuration.
 * For V2 workflows without a schema, skip default hide rules since
 * the backend already curates output_files to real deliverables.
 */
export function filterOutputFiles<T extends { s3_uri?: string; name?: string; type?: string; extension?: string }>(
  files: T[],
  uiSchema: WorkflowUiSchema | null,
  options?: { isV2?: boolean }
): T[] {
  // V2 + no schema = trust backend curation, show everything
  if (options?.isV2 && !uiSchema) {
    return files;
  }
  return files.filter(file => {
    const fileType = file.type || 
      file.extension?.toLowerCase() ||
      file.s3_uri?.split('.').pop()?.toLowerCase() || 
      file.name?.split('.').pop()?.toLowerCase() || 
      '';
    return shouldShowOutputType(uiSchema, fileType);
  });
}

/**
 * Get the primary output file from the list
 * Returns the first file matching the primary output type, or the first file
 */
export function getPrimaryOutputFile<T extends { s3_uri?: string; name?: string; type?: string; extension?: string }>(
  files: T[],
  uiSchema: WorkflowUiSchema | null
): T | undefined {
  const handling = getOutputHandling(uiSchema);
  
  if (handling.primaryOutputType) {
    const primaryFile = files.find(file => {
      const fileType = file.type || 
        file.extension?.toLowerCase() ||
        file.s3_uri?.split('.').pop()?.toLowerCase() || 
        file.name?.split('.').pop()?.toLowerCase() || 
        '';
      return fileType === handling.primaryOutputType;
    });
    if (primaryFile) return primaryFile;
  }
  
  return files[0];
}

/**
 * Generate download filename based on UI schema template
 * Replaces: hardcoded getDownloadFileName() implementations
 * 
 * Template variables:
 * - {job_number} - The job number/name
 * - {workflow} - The workflow ID
 * - {practice} - The practice area
 * - {extension} - The file extension
 * - {original_name} - The original filename
 * - {date} - Current date (YYYY-MM-DD)
 */
export function getDownloadFilename(
  file: { name?: string; s3_uri?: string },
  uiSchema: WorkflowUiSchema | null,
  context: {
    jobNumber?: string;
    workflow?: string;
    practice?: string;
  }
): string {
  const handling = getOutputHandling(uiSchema);
  
  // Get original filename and extension
  const originalName = file.name || file.s3_uri?.split('/').pop() || 'download';
  const extension = originalName.split('.').pop() || '';
  const nameWithoutExt = originalName.replace(`.${extension}`, '');
  
  // If no template, return original name
  if (!handling.downloadFilenameTemplate) {
    return originalName;
  }
  
  // Apply template
  const today = new Date().toISOString().split('T')[0];
  return handling.downloadFilenameTemplate
    .replace('{job_number}', context.jobNumber || 'job')
    .replace('{workflow}', context.workflow || 'workflow')
    .replace('{practice}', context.practice || 'practice')
    .replace('{extension}', extension)
    .replace('{original_name}', nameWithoutExt)
    .replace('{date}', today);
}

/**
 * Get next workflow information from UI schema
 * Returns null if no next workflow is configured
 */
export function getNextWorkflow(uiSchema: WorkflowUiSchema | null): { practice: string; workflow: string } | null {
  const config = getMultiStepConfig(uiSchema);
  return config.nextWorkflow || null;
}

/**
 * Check if the workflow should show a review form
 * Replaces: canShowReviewScreen() hardcoded logic
 */
export function shouldShowReviewForm(
  uiSchema: WorkflowUiSchema | null,
  executionStatus: string,
  isBackendChaining?: boolean
): boolean {
  // Don't show review if backend is handling auto-chaining
  if (isBackendChaining) {
    return false;
  }
  
  // Must be a multi-step workflow
  if (!isMultiStepWorkflow(uiSchema)) {
    return false;
  }
  
  // Must require review
  if (!requiresReview(uiSchema)) {
    return false;
  }
  
  // Must be in a completed state
  return ['SUCCEEDED', 'WAITING_FOR_INPUT', 'COMPLETED'].includes(executionStatus);
}

/**
 * Get review form configuration from UI schema
 */
export function getReviewFormConfig(uiSchema: WorkflowUiSchema | null) {
  if (!uiSchema?.review_form?.enabled) {
    return null;
  }
  
  return {
    title: uiSchema.review_form.title,
    description: uiSchema.review_form.description,
    showSourceDocument: uiSchema.review_form.show_source_document,
    submitButtonText: uiSchema.review_form.submit_button_text,
    cancelButtonText: uiSchema.review_form.cancel_button_text,
    saveButtonText: uiSchema.review_form.save_button_text,
    sections: uiSchema.review_form.sections,
  };
}

/**
 * Get help text from UI schema or recipe
 * Returns undefined if no help text is configured
 */
export function getWorkflowHelpText(uiSchema: WorkflowUiSchema | null): string | undefined {
  // Check for help_text in UI schema (new format)
  const anySchema = uiSchema as any;
  if (anySchema?.help_text?.overview) {
    return anySchema.help_text.overview;
  }
  
  // Check for sample_instructions (alternate format)
  if (anySchema?.help_text?.sample_instructions) {
    return anySchema.help_text.sample_instructions;
  }
  
  return undefined;
}

/**
 * Get workflow display name from UI schema
 */
export function getWorkflowDisplayName(uiSchema: WorkflowUiSchema | null): string | undefined {
  return uiSchema?.metadata?.display_name;
}

/**
 * Get workflow description from UI schema
 */
export function getWorkflowDescription(uiSchema: WorkflowUiSchema | null): string | undefined {
  return uiSchema?.metadata?.description;
}

/**
 * Determine execution steps based on workflow configuration
 * Replaces: hardcoded getExecutionSteps() with 6-step logic
 */
export function getExecutionSteps(
  uiSchema: WorkflowUiSchema | null,
  chainedInfo?: { isChainedWorkflow?: boolean; totalSteps?: number }
): string[] {
  // Check UI schema for custom steps
  const anySchema = uiSchema as any;
  if (anySchema?.execution_steps) {
    return anySchema.execution_steps;
  }
  
  // Handle chained workflows
  if (chainedInfo?.isChainedWorkflow && chainedInfo?.totalSteps === 2) {
    return [
      'Upload Files',
      'Step 1: Categorize',
      'Step 1: Process', 
      'Step 2: Extract',
      'Step 2: Structure',
      'Generate Results'
    ];
  }
  
  // Default steps
  return ['File Upload', 'Load Recipe', 'Extract Text', 'AI Processor', 'Generate Results'];
}

/**
 * Check if unified workflow display is enabled
 */
export function isUnifiedWorkflowEnabled(uiSchema: WorkflowUiSchema | null): boolean {
  return uiSchema?.unified_workflow?.enabled === true;
}

/**
 * Get unified workflow configuration
 */
export function getUnifiedWorkflowConfig(uiSchema: WorkflowUiSchema | null) {
  return uiSchema?.unified_workflow || null;
}

/**
 * Get step controls configuration
 */
export function getStepControls(uiSchema: WorkflowUiSchema | null) {
  return {
    allowIntermediateDownload: uiSchema?.step_controls?.allow_intermediate_download ?? false,
    downloadableTypes: uiSchema?.step_controls?.downloadable_types ?? ['json'],
    allowIntermediateEdit: uiSchema?.step_controls?.allow_intermediate_edit ?? false,
    allowManualTrigger: uiSchema?.step_controls?.allow_manual_trigger ?? false,
    downloadButtonText: uiSchema?.step_controls?.download_button_text ?? 'Download Data',
    editButtonText: uiSchema?.step_controls?.edit_button_text ?? 'Edit Data',
    triggerButtonText: uiSchema?.step_controls?.trigger_button_text ?? 'Continue to Next Step',
  };
}

/**
 * Get step display configuration
 */
export function getStepDisplayConfig(uiSchema: WorkflowUiSchema | null) {
  return {
    showStepProgress: uiSchema?.step_display?.show_step_progress ?? true,
    progressStyle: uiSchema?.step_display?.progress_style ?? 'pills',
    stepLabels: uiSchema?.step_display?.step_labels,
  };
}

/**
 * Check if JSON download is allowed (either via step_controls or output_handling)
 */
export function allowsJsonDownload(uiSchema: WorkflowUiSchema | null): boolean {
  // Check step_controls for intermediate download
  const stepControls = getStepControls(uiSchema);
  if (stepControls.allowIntermediateDownload && stepControls.downloadableTypes.includes('json')) {
    return true;
  }
  
  // Check output_handling for explicit json download allowance
  if (uiSchema?.output_handling?.allow_json_download === true) {
    return true;
  }
  
  // Check if json is in show_download_for_types
  if (uiSchema?.output_handling?.show_download_for_types?.includes('json')) {
    return true;
  }
  
  return false;
}

/**
 * Get all output files including hidden ones (for manual download feature)
 */
export function getAllOutputFiles<T extends { s3_uri?: string; name?: string; type?: string }>(
  files: T[],
  uiSchema: WorkflowUiSchema | null,
  options?: { types?: string[] }
): T[] {
  if (!options?.types) {
    return files;
  }
  
  return files.filter(file => {
    const fileType = file.type || 
      file.s3_uri?.split('.').pop()?.toLowerCase() || 
      file.name?.split('.').pop()?.toLowerCase() || 
      '';
    return options.types!.includes(fileType.toLowerCase());
  });
}

// get input display config, with sensible defaults
export function getInputDisplayConfig(uiSchema: WorkflowUiSchema | null): InputDisplayConfig {
  const config = uiSchema?.input_display;
  return {
    show_original_documents: config?.show_original_documents ?? true,
    viewer_position: config?.viewer_position ?? 'side',
    allow_input_download: config?.allow_input_download ?? false,
    panel_title: config?.panel_title ?? 'Original Document',
  };
}

// whether to show the original document viewer based on schema config
export function shouldShowOriginalDocuments(uiSchema: WorkflowUiSchema | null): boolean {
  const config = getInputDisplayConfig(uiSchema);
  return config.show_original_documents !== false && config.viewer_position !== 'hidden';
}

// get file upload config from schema
export function getFileUploadConfig(uiSchema: WorkflowUiSchema | null): FileUploadConfig {
  return uiSchema?.file_upload ?? {};
}
